import { ListItemIcon, ListItemText, MenuItem, Stack } from "@mui/material";
import { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

import InviteCustomerAsUserModal from "@/App/MainAppView/Customers/components/InviteCustomerAsUserModal";
import { ROUTE_PATH } from "@/common/constants/routing";
import { AppPermission, CustomerDto } from "@/core/api/generated/v1";

import AuthorizedElement from "../../Auth/AuthorizedElement";
import AuthorizedMenuItem from "../../Auth/AuthorizedMenuItem";
import EntityMenu from "../../EntityMenu/EntityMenu";
import AppIcon from "../../Icons/AppIcon";
import AppLink from "../../Link/AppLink";
import HeaderMenuItem from "../../Menu/HeaderMenuItem";
import CustomerDeleteModal from "./CustomerDeleteModal";

enum ActionFlags {
  Edit = "Edit",
  InviteAsUser = "InviteAsUser",
  Delete = "Delete",
}
const defaultDisplayProps = {
  actions: {
    edit: true,
    inviteAsUser: true,
    delete: true,
    createCharge: true,
  },
};

interface TriggersProps {
  entity?: CustomerDto | null;
  definition: typeof ActionFlags;
  handleClose: () => void;
  startAction: (props: ActionFlags) => void;
}

interface Props {
  entity?: CustomerDto | null;
  disabled?: boolean;
  onUpdate: ((newValue: CustomerDto | null | undefined) => void) | undefined;
  onDelete?: (() => void) | undefined;
  displayProps?: Partial<typeof defaultDisplayProps>;
  customActions?: (props: TriggersProps) => ReactNode;
}

export default function CustomerMenu({
  entity,
  disabled,
  customActions,
  onUpdate,
  onDelete,
  displayProps = defaultDisplayProps,
}: Props) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };
  return (
    <EntityMenu
      definition={ActionFlags}
      entity={entity}
      disabled={disabled}
      actionTriggers={({ handleClose, startAction }) => (
        <>
          {customActions &&
            customActions({
              entity,
              definition: ActionFlags,
              handleClose,
              startAction,
            })}
          {displayProps?.actions?.edit && (
            <AuthorizedMenuItem
              permissions={[AppPermission.CustomerManage]}
              component={RouterLink}
              to={ROUTE_PATH.CUSTOMER_EDIT(entity?.id)}
            >
              <ListItemIcon>
                <AppIcon of='edit' fontSize='small' />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </AuthorizedMenuItem>
          )}

          {displayProps?.actions?.inviteAsUser && (
            <AuthorizedMenuItem
              permissions={[AppPermission.CustomerManage]}
              onClick={() => startAction(ActionFlags.InviteAsUser)}
            >
              <ListItemIcon>
                <AppIcon of='invite' fontSize='small' />
              </ListItemIcon>
              <ListItemText>Invite as user</ListItemText>
            </AuthorizedMenuItem>
          )}

          {displayProps?.actions?.delete && (
            <AuthorizedMenuItem
              permissions={[AppPermission.CustomerManage]}
              onClick={() => startAction(ActionFlags.Delete)}
            >
              <ListItemIcon>
                <AppIcon of='delete' fontSize='small' />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </AuthorizedMenuItem>
          )}

          {displayProps?.actions?.createCharge && <HeaderMenuItem primaryTitle='Quick actions' />}

          {displayProps?.actions?.createCharge && (
            <AuthorizedElement permissions={[AppPermission.ChargeManage]}>
              <MenuItem
                component={AppLink}
                to={ROUTE_PATH.CHARGE_CREATE({
                  customerId: entity?.id,
                })}
              >
                <ListItemIcon>
                  <AppIcon of='charge' fontSize='small' />
                </ListItemIcon>
                <ListItemText>New charge</ListItemText>
              </MenuItem>
            </AuthorizedElement>
          )}
        </>
      )}
      actionHandlers={({ currentAction, cancelAction, completeAction }) => (
        <Stack>
          {/* Delete entity */}
          {entity && (
            <CustomerDeleteModal
              entity={entity}
              open={currentAction === ActionFlags.Delete}
              onClose={() => cancelAction()}
              onDelete={() => {
                onDelete && onDelete();
                completeAction();
              }}
            />
          )}

          {/* Invite as user */}
          {entity && (
            <InviteCustomerAsUserModal
              customer={entity}
              open={currentAction === ActionFlags.InviteAsUser}
              onClose={() => cancelAction()}
            />
          )}
        </Stack>
      )}
    />
  );
}
